/**
 * ActionProposalCard — TASK-593 Phase D.
 *
 * Renders a single signal→task proposal (from the `propose_action` tool) as an
 * Apply/Skip card. Apply persists the task to the action spine via POST /api/tasks
 * (the same endpoint the whole task system reads from); Skip discards it. Nothing
 * is written until the advisor clicks Apply — the human gate for the `review`
 * trust gradient.
 */

import React, { useState } from 'react';
import { CheckCircle, X, Loader2, ClipboardList } from 'lucide-react';
import { useCreateTask } from '../../../hooks/useTasksQuery';
import { useAccount } from '../../../contexts/AccountContext';
import { buildTaskRequest } from '../../../utils/buildTaskRequest';
import type { ProposalActionItem } from '../../../types/prepChat.types';
import type { TaskPriority, TaskType } from '../../../types/tasks';

interface ActionProposalCardProps {
  proposal: ProposalActionItem;
}

// Must match backend TaskType (task_enums.py). A value outside this set 422s POST /api/tasks.
const VALID_TASK_TYPES: readonly string[] = [
  'follow_up_call', 'follow_up_email', 'follow_up_meeting', 'follow_up_document',
  'follow_up_other', 'task_general', 'task_review', 'task_compliance',
  'task_research', 'task_admin',
];

const PRIORITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

export const ActionProposalCard: React.FC<ActionProposalCardProps> = ({ proposal }) => {
  const createTask = useCreateTask();
  const { user } = useAccount();
  const [state, setState] = useState<'pending' | 'applied' | 'skipped'>('pending');

  const data = (proposal.data ?? {}) as Record<string, unknown>;
  const title = String(data.title ?? 'Follow-up task');
  const rationale = String(data.rationale ?? '');
  const priority = String(data.priority ?? 'medium');
  const dueDate = data.due_date ? String(data.due_date) : undefined;

  const handleApply = () => {
    const rawType = String(data.task_type ?? '');
    const task_type = (VALID_TASK_TYPES.includes(rawType) ? rawType : 'follow_up_other') as TaskType;
    // buildTaskRequest owns the shared shape (title cap + reminder 1 day before due).
    const req = buildTaskRequest({
      title,
      description: rationale || undefined,
      task_type,
      priority: priority as TaskPriority,
      due_date: dueDate,
      client_id: data.client_id ? String(data.client_id) : undefined,
      related_entity_type: data.related_entity_type ? String(data.related_entity_type) : undefined,
      related_entity_id: data.related_entity_id ? String(data.related_entity_id) : undefined,
      source: data.source ? String(data.source) : undefined,
      assignee_user_id: user?.id,
    });
    createTask.mutate(req, { onSuccess: () => setState('applied') });
  };

  if (state === 'skipped') return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start gap-2">
        <ClipboardList className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">{title}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY_STYLES[priority] ?? PRIORITY_STYLES.medium}`}>
              {priority}
            </span>
          </div>
          {rationale && <p className="mt-1 text-xs text-gray-500">{rationale}</p>}
          {dueDate && (
            <p className="mt-1 text-[11px] text-gray-400">Due {new Date(dueDate).toLocaleDateString()}</p>
          )}
        </div>
      </div>

      {state === 'applied' ? (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-green-600">
          <CheckCircle className="h-3.5 w-3.5" /> Task added
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={handleApply}
            disabled={createTask.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {createTask.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
            Apply
          </button>
          <button
            onClick={() => setState('skipped')}
            disabled={createTask.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            <X className="h-3.5 w-3.5" /> Skip
          </button>
          {createTask.isError && <span className="text-xs text-red-500">Could not add task</span>}
        </div>
      )}
    </div>
  );
};

export default ActionProposalCard;
